import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Trophy, Target, TrendingUp, TrendingDown, Coins, BarChart3, Loader2, User } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserStatsModal({ userId, username, avatarUrl, isOpen, onClose }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && userId) {
      fetchUserStats();
    }
  }, [isOpen, userId]);

  const fetchUserStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/users/${userId}/profile`);
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch user stats:', err);
      setError('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  const formatGameType = (type) => {
    const types = {
      coinflip: 'Coinflip',
      coinflip_p2p: 'Coinflip P2P',
      crash: 'Crash',
      crash_multiplayer: 'Crash',
      roulette: 'Roulette',
      roulette_multiplayer: 'Roulette',
      blackjack: 'Blackjack',
      blackjack_p2p: 'Blackjack P2P',
      cases: 'Cases'
    };
    return types[type] || type;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
            
            <div className="flex items-center gap-4">
              {avatarUrl ? (
                <img 
                  src={avatarUrl} 
                  alt={username} 
                  className="w-20 h-20 rounded-full border-4 border-white/20"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-slate-700 flex items-center justify-center border-4 border-white/20">
                  <User size={40} className="text-slate-400" />
                </div>
              )}
              <div>
                <h2 className="text-2xl font-bold text-white">{username}</h2>
                {stats?.roblox_username && (
                  <p className="text-blue-200">Roblox: {stats.roblox_username}</p>
                )}
                {stats?.created_at && (
                  <p className="text-blue-200/70 text-sm">
                    Joined {new Date(stats.created_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[50vh]">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
              </div>
            ) : error ? (
              <div className="text-center py-8 text-red-400">{error}</div>
            ) : stats?.stats ? (
              <div className="space-y-4">
                {/* Main Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
                    <div className="flex items-center gap-2 mb-1">
                      <Target className="text-blue-400" size={16} />
                      <span className="text-slate-400 text-xs">Total Games</span>
                    </div>
                    <p className="text-xl font-bold text-white">{stats.stats.total_games}</p>
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
                    <div className="flex items-center gap-2 mb-1">
                      <Trophy className="text-yellow-400" size={16} />
                      <span className="text-slate-400 text-xs">Win Rate</span>
                    </div>
                    <p className="text-xl font-bold text-yellow-400">{stats.stats.win_rate}%</p>
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
                    <div className="flex items-center gap-2 mb-1">
                      <Coins className="text-green-400" size={16} />
                      <span className="text-slate-400 text-xs">Total Wagered</span>
                    </div>
                    <p className="text-xl font-bold text-green-400">R${stats.stats.total_wagered?.toLocaleString()}</p>
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
                    <div className="flex items-center gap-2 mb-1">
                      <BarChart3 className={stats.stats.net_profit >= 0 ? 'text-green-400' : 'text-red-400'} size={16} />
                      <span className="text-slate-400 text-xs">Net Profit</span>
                    </div>
                    <p className={`text-xl font-bold ${stats.stats.net_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {stats.stats.net_profit >= 0 ? '+' : ''}R${stats.stats.net_profit?.toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Win/Loss */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/20">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="text-green-400" size={16} />
                      <span className="text-green-400/70 text-xs">Wins</span>
                    </div>
                    <p className="text-xl font-bold text-green-400">{stats.stats.wins}</p>
                    <p className="text-green-400/70 text-sm">+R${stats.stats.total_won?.toLocaleString()}</p>
                  </div>
                  
                  <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/20">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingDown className="text-red-400" size={16} />
                      <span className="text-red-400/70 text-xs">Losses</span>
                    </div>
                    <p className="text-xl font-bold text-red-400">{stats.stats.losses}</p>
                    <p className="text-red-400/70 text-sm">-R${stats.stats.total_lost?.toLocaleString()}</p>
                  </div>
                </div>

                {/* Game Breakdown */}
                {stats.stats.game_stats && Object.keys(stats.stats.game_stats).length > 0 && (
                  <div>
                    <h3 className="text-white font-semibold mb-3">Game Breakdown</h3>
                    <div className="space-y-2">
                      {Object.entries(stats.stats.game_stats).map(([game, data]) => (
                        <div 
                          key={game} 
                          className="bg-slate-800/30 rounded-lg p-3 flex items-center justify-between"
                        >
                          <span className="text-white font-medium capitalize">{formatGameType(game)}</span>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-slate-400">{data.wins}W / {data.losses}L</span>
                            <span className={data.won - data.lost >= 0 ? 'text-green-400' : 'text-red-400'}>
                              {data.won - data.lost >= 0 ? '+' : ''}R${(data.won - data.lost).toFixed(0)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">No stats available</div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Clickable Avatar Component
export function ClickableAvatar({ userId, username, avatarUrl, size = 64, className = '', borderColor = 'border-slate-600' }) {
  const [showStats, setShowStats] = useState(false);

  return (
    <>
      <button
        onClick={() => setShowStats(true)}
        className={`rounded-full overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all ${className}`}
        title={`View ${username}'s stats`}
      >
        {avatarUrl ? (
          <img 
            src={avatarUrl} 
            alt={username} 
            className={`object-cover border-2 ${borderColor} rounded-full`}
            style={{ width: size, height: size }}
          />
        ) : (
          <div 
            className={`bg-slate-700 flex items-center justify-center border-2 ${borderColor} rounded-full`}
            style={{ width: size, height: size }}
          >
            <User size={size * 0.5} className="text-slate-400" />
          </div>
        )}
      </button>
      
      <UserStatsModal
        userId={userId}
        username={username}
        avatarUrl={avatarUrl}
        isOpen={showStats}
        onClose={() => setShowStats(false)}
      />
    </>
  );
}
